import React, { useState } from 'react';
import { X } from 'lucide-react';
import C from '../../constants/colors';

export default function CompanyFormModal({ company, employees = [], onSave, onClose }) {
  const [form, setForm] = useState({
    name: company?.name || '',
    industry: company?.industry || '',
    lat: company?.lat || '',
    lng: company?.lng || '',
    picName: company?.picName || '',
    picPhone: company?.picPhone || '',
    security: company?.emgSecurity || '',
    ambulance: company?.emgAmbulance || '',
    police: company?.emgPolice || '',
    civilDefense: company?.emgCivil || '',
    timeAttendanceEnabled: company?.timeAttendanceEnabled || false,
    hseTrainingEnabled: company?.hseTrainingEnabled || false,
  });
  const [errors, setErrors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  // When editing: show only this company's employees. When creating: show all employees.
  const companyEmployees = company?.id
    ? employees.filter(e => e.companyId === company.id)
    : employees;
  const required = ['name'];
  const labels = { name: 'Company Name' };

  async function handleSubmit() {
    const missing = required.filter(k => !String(form[k]).trim());
    if (missing.length) { setErrors(missing.map(k => labels[k])); return; }
    setSaving(true);
    setSaveError('');
    try {
      await onSave({
        ...(company?.id ? { id: company.id } : {}),
        name: form.name.trim(),
        industry: form.industry.trim(),
        lat: parseFloat(form.lat) || 0,
        lng: parseFloat(form.lng) || 0,
        picName: form.picName.trim(),
        picPhone: form.picPhone.trim(),
        emgSecurity: form.security.trim(),
        emgAmbulance: form.ambulance.trim(),
        emgPolice: form.police.trim(),
        emgCivil: form.civilDefense.trim(),
        timeAttendanceEnabled: form.timeAttendanceEnabled,
        hseTrainingEnabled: form.hseTrainingEnabled,
      });
      onClose();
    } catch (err) {
      setSaveError(err?.response?.data?.error || 'Failed to save company — please try again.');
    } finally {
      setSaving(false);
    }
  }

  const inp = (key, label, placeholder = '') => (
    <div style={{ marginBottom: '14px' }}>
      <label style={{ display: 'block', fontSize: '11px', color: C.textMuted, marginBottom: '5px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}{required.includes(key) ? ' *' : ''}
      </label>
      <input value={form[key]} onChange={e => setForm(p => ({...p, [key]: e.target.value}))}
        placeholder={placeholder}
        style={{ width: '100%', background: C.surface, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '8px 12px', color: C.textPrimary, fontSize: '13px', fontFamily: 'inherit', outline: 'none' }}
      />
    </div>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: C.surfaceElevated, border: `1px solid ${C.border}`, borderRadius: '14px', padding: '28px', width: '520px', maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h3 style={{ fontSize: '18px', fontWeight: 700, color: C.textPrimary }}>{company ? 'Edit Company' : 'Add Company'}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted }}><X size={18} /></button>
        </div>
        {errors.length > 0 && <div style={{ background: C.red + '22', border: `1px solid ${C.red}44`, borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', fontSize: '12px', color: C.red }}>Missing required fields: {errors.join(', ')}</div>}
        {saveError && <div style={{ background: C.red + '22', border: `1px solid ${C.red}44`, borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', fontSize: '12px', color: C.red }}>{saveError}</div>}
        {inp('name', 'Company Name', 'e.g. Acme Corporation')}
        {inp('industry', 'Industry', 'e.g. Manufacturing')}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          {inp('lat', 'HQ Latitude', '25.2048')}{inp('lng', 'HQ Longitude', '55.2708')}
        </div>
        <div style={{ borderTop: `1px solid ${C.border}`, margin: '16px 0 16px' }} />
        <div style={{ fontSize: '12px', color: C.textMuted, marginBottom: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Person In Charge</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <div style={{ marginBottom: '14px' }}>
            <label style={{ display: 'block', fontSize: '11px', color: C.textMuted, marginBottom: '5px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Name</label>
            <select
              value={form.picName}
              onChange={e => {
                const emp = companyEmployees.find(emp => emp.name === e.target.value);
                setForm(f => ({ ...f, picName: e.target.value, picPhone: emp?.phone || '' }));
              }}
              style={{ width: '100%', background: C.surface, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '8px 12px', color: form.picName ? C.textPrimary : C.textMuted, fontSize: '13px', fontFamily: 'inherit', outline: 'none' }}
            >
              <option value="">— Select employee —</option>
              {companyEmployees.map(emp => (
                <option key={emp.id} value={emp.name}>{emp.name}</option>
              ))}
            </select>
          </div>
          {inp('picPhone', 'Phone', '+971501234567')}
        </div>
        <div style={{ borderTop: `1px solid ${C.border}`, margin: '16px 0 16px' }} />
        <div style={{ fontSize: '12px', color: C.textMuted, marginBottom: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Emergency Numbers</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          {inp('security', 'Security', '')}{inp('ambulance', 'Ambulance', '')}
          {inp('police', 'Police', '')}{inp('civilDefense', 'Civil Defense', '')}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: C.surface, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '12px 14px', marginBottom: '20px', marginTop: '20px' }}>
          <div>
            <div style={{ fontSize: '13px', fontWeight: 600, color: C.textPrimary }}>Time & Attendance</div>
            <div style={{ fontSize: '11px', color: C.textMuted, marginTop: '2px' }}>Enable time & attendance tracking for employees of this company</div>
          </div>
          <div onClick={() => setForm(p => ({...p, timeAttendanceEnabled: !p.timeAttendanceEnabled}))}
            style={{ width: '42px', height: '24px', borderRadius: '12px', background: form.timeAttendanceEnabled ? C.blue : C.border, cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0, marginLeft: '12px' }}>
            <div style={{ position: 'absolute', top: '3px', left: form.timeAttendanceEnabled ? '21px' : '3px', width: '18px', height: '18px', borderRadius: '50%', background: '#fff', transition: 'left 0.2s' }} />
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: C.surface, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '12px 14px', marginBottom: '20px' }}>
          <div>
            <div style={{ fontSize: '13px', fontWeight: 600, color: C.textPrimary }}>HSE Training</div>
            <div style={{ fontSize: '11px', color: C.textMuted, marginTop: '2px' }}>Enable HSE training course management for this company</div>
          </div>
          <div onClick={() => setForm(p => ({...p, hseTrainingEnabled: !p.hseTrainingEnabled}))}
            style={{ width: '42px', height: '24px', borderRadius: '12px', background: form.hseTrainingEnabled ? C.blue : C.border, cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0, marginLeft: '12px' }}>
            <div style={{ position: 'absolute', top: '3px', left: form.hseTrainingEnabled ? '21px' : '3px', width: '18px', height: '18px', borderRadius: '50%', background: '#fff', transition: 'left 0.2s' }} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
          <button onClick={onClose} style={{ padding: '9px 20px', borderRadius: '8px', border: `1px solid ${C.border}`, background: 'transparent', color: C.textSecondary, fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
          <button onClick={handleSubmit} disabled={saving} style={{ padding: '9px 20px', borderRadius: '8px', border: 'none', background: C.blue, color: '#fff', fontSize: '13px', fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer', fontFamily: 'inherit', opacity: saving ? 0.6 : 1 }}>{saving ? 'Saving…' : 'Save Company'}</button>
        </div>
      </div>
    </div>
  );
}
